import { useEffect, useState } from 'react';
import * as Contacts from 'expo-contacts';
import type { PlayerContactProps, PublicPlayerProps } from '../../types';
import { ContactsApi, ContactsHelpers } from './api';
import * as SMS from 'expo-sms';

type UseContactProps = {
    referral_code?:string
}

const useContacts = ({ referral_code }:UseContactProps) => {
    const [ permission_state, setPermissionState ] = useState<{
        permission_checked:boolean,
        permission?:Contacts.PermissionResponse
    }>({
        permission_checked:false
    });
    const [ contact_loading, setContactLoading ] = useState(false);
    const { permission_checked, permission } = permission_state;
    const [ available_state, setAvailableState ] = useState<{
        is_available_checked:boolean,
        is_available: boolean
    }>({
        is_available_checked: false,
        is_available: false
    });
    const { is_available_checked, is_available } = available_state;

    useEffect(() => {
        ContactsApi.setEnvironment()
        launch()
    },[]);

    const launch = async() => {
        if(is_available_checked){ return } //Already checked!
        let avail = await checkAvailable();
        if(!avail){ return }
        await checkPermissions();
    }

    const checkAvailable = async():Promise<boolean> => {
        try {
            const available = await Contacts.isAvailableAsync();
            setAvailableState({
                is_available_checked: true,
                is_available: available
            })
            return available
        } catch (e) {
            setAvailableState({ is_available_checked:false, is_available: false });
            return false
        }
    }

    const compareContacts = async(player_id:string):Promise<PlayerContactProps[]> => {
        if(!player_id){ return [] }
        const local_contacts = await getContacts();
        if(!local_contacts){ return [] }

        let local_pcs:PlayerContactProps[] = []
        local_contacts.data.map(d => {
            let pc:PlayerContactProps = {
                player_contact_id: '',
                external_id: d.id,
                player_id:player_id??'0',
                first_name: d.firstName,
                last_name: d.lastName,
                type: 'native',
                create_datetime: '',
                last_update_datetime:''
            }
            if(d.phoneNumbers){
                let phone_number = ContactsHelpers.formatContactPhoneNumber(d);
                if(!phone_number){ return }
                local_pcs.push({ ...pc, sub_type: 'phone', phone: phone_number })
            }
            if(d.emails){
                let primary_email = d.emails.find(e => e.isPrimary)?.email
                if(primary_email){ primary_email = d.emails[0]?.email }
                if(!primary_email){ return }
                local_pcs.push({ ...pc, sub_type:'email', email: primary_email.toLowerCase() })
            }
        })
        const player_contacts = await ContactsApi.compareContacts(local_pcs);
        return player_contacts
    }

    const smsInviteContacts = async(pcs:PlayerContactProps[], message:string):Promise<{ allowed:boolean, result: string }> => {
        try {
            let phones = pcs.filter(pc => pc.phone).map(pc => pc.phone ?? '')
            const isAvailable = await SMS.isAvailableAsync();
            if (isAvailable) {
                const result = await SMS.sendSMSAsync(phones, message);
                return { allowed: true, result: result.result }
            } else {
                // misfortune... there's no SMS available on this device
                return { allowed: false, result: 'error' }
            }
        } catch (e) {
            return { allowed: false, result: 'error' }
        }
       
    }

    const checkPermissions = async():Promise<Contacts.PermissionResponse | undefined> => {
        try {
            const response = await Contacts.getPermissionsAsync();
            setPermissionState({
                permission_checked:true,
                permission: response
            });
            return response
        } catch (e) {
            setPermissionState({ permission_checked: true });
            return undefined
        }
    }

    const requestPermissions = async():Promise<Contacts.PermissionResponse | undefined> => {
        try {
            const response = await Contacts.requestPermissionsAsync();
            setPermissionState({
                permission_checked: true,
                permission: response
            });
            return response
        } catch (e) {
            return undefined
        }
    }

    const getContacts = async():Promise<Contacts.ContactResponse | undefined> => {
        try {
            const resp = await Contacts.getContactsAsync()
            return resp
        } catch (e) {
            console.log(e);
            return undefined
        }
    }

    const getContact = async():Promise<{ player:PublicPlayerProps, number:string, new_player:boolean } | undefined> => {
        try {
            let new_player = false
            if(!permission?.granted){ return undefined } //We do not have permission
            setContactLoading(true);
            const contact = await Contacts.presentContactPickerAsync();
            if(!contact){ 
                setContactLoading(false);
                return undefined 
            }
            let number = ContactsHelpers.formatContactPhoneNumber(contact);

            if(!number){ 
                setContactLoading(false);
                return undefined 
            }
            //Lets check for the player by phone!
            let player = await ContactsApi.getPlayerByPhone(number);
            if(!player){
                //ok we need to save the number so we can grab a player id for it
                const gen_response = await ContactsApi.generatePlayerByPhone(number, referral_code);
                if(!gen_response){ 
                    setContactLoading(false);
                    return undefined 
                } //We failed!
                player = await ContactsApi.getPlayerById(gen_response.player_id);
                if(!player){ 
                    setContactLoading(false);
                    return undefined 
                }
                new_player = true
            }
            setContactLoading(false);
            return { player, number, new_player }
            
        } catch (e) {
            return undefined
        }
    }

    return { is_available_checked, is_available, permission_checked, permission, contact_loading, smsInviteContacts, compareContacts, getContacts, checkPermissions, checkAvailable, requestPermissions, getContact }

}

export { useContacts }